/**
 * Clinic Service
 * Business logic layer for clinic operations
 */

import { clinicApi } from '../api/endpoints/clinic.api';
import type {
  ClinicProfile,
  ClinicOnboardingData,
  ClinicStatus,
  ClinicDoctorAssociation,
  ClinicStaffAssociation,
  ClinicStats,
  ClinicActivity,
  ClinicDoctorStatus,
  ClinicDoctorRole,
  ClinicStaffStatus,
} from '../types/clinic.types';

/**
 * Clinic Service
 * Handles clinic business logic
 */
export class ClinicService {
  /**
   * Get current authenticated user's clinic
   */
  static async getMe(): Promise<ClinicProfile> {
    try {
      const clinic = await clinicApi.getMe();
      return clinic;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get clinic by ID
   */
  static async getById(id: string): Promise<ClinicProfile> {
    try {
      const clinic = await clinicApi.getById(id);
      return clinic;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update clinic status
   */
  static async updateStatus(status: ClinicStatus): Promise<ClinicProfile> {
    try {
      const clinic = await clinicApi.updateStatus(status);
      return clinic;
    } catch (error) {
      throw error;
    }
  }

  // Onboarding Steps

  /**
   * Step 1: Update clinic information
   */
  static async updateClinicInfo(data: Partial<ClinicOnboardingData>): Promise<ClinicProfile> {
    try {
      const clinic = await clinicApi.updateClinicInfo(data);
      return clinic;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Step 2: Update primary address
   */
  static async updatePrimaryAddress(data: Partial<ClinicOnboardingData>): Promise<ClinicProfile> {
    try {
      const clinic = await clinicApi.updatePrimaryAddress(data);
      return clinic;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Step 3: Update contact person
   */
  static async updateContactPerson(data: Partial<ClinicOnboardingData>): Promise<ClinicProfile> {
    try {
      const clinic = await clinicApi.updateContactPerson(data);
      return clinic;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Step 4: Update licensing information
   */
  static async updateLicensing(data: Partial<ClinicOnboardingData>): Promise<ClinicProfile> {
    try {
      const clinic = await clinicApi.updateLicensing(data);
      return clinic;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Step 5: Update services provided
   */
  static async updateServicesProvided(data: Partial<ClinicOnboardingData>): Promise<ClinicProfile> {
    try {
      const clinic = await clinicApi.updateServicesProvided(data);
      return clinic;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Step 6: Update operations schedule
   */
  static async updateOperations(data: Partial<ClinicOnboardingData>): Promise<ClinicProfile> {
    try {
      const clinic = await clinicApi.updateOperations(data);
      return clinic;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Step 7: Update insurance billing
   */
  static async updateInsuranceBilling(data: Partial<ClinicOnboardingData>): Promise<ClinicProfile> {
    try {
      const clinic = await clinicApi.updateInsuranceBilling(data);
      return clinic;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Step 8: Upload documents
   */
  static async uploadDocuments(data: {
    logo?: {
      fileName: string;
      bucket: string;
      key: string;
      baseUrl: string;
      cdnUrl: string;
      contentType: string;
      fileSize: number;
      s3Url: string;
    };
    businessLicense?: {
      fileName: string;
      bucket: string;
      key: string;
      baseUrl: string;
      cdnUrl: string;
      contentType: string;
      fileSize: number;
      s3Url: string;
    };
    w9Form?: {
      fileName: string;
      bucket: string;
      key: string;
      baseUrl: string;
      cdnUrl: string;
      contentType: string;
      fileSize: number;
      s3Url: string;
    };
  }): Promise<ClinicProfile> {
    try {
      const clinic = await clinicApi.uploadDocuments(data);
      return clinic;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Step 9: Update terms and compliance
   */
  static async updateTermsCompliance(data: Partial<ClinicOnboardingData>): Promise<ClinicProfile> {
    try {
      const clinic = await clinicApi.updateTermsCompliance(data);
      return clinic;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Step 10: Review and submit
   */
  static async reviewSubmit(data?: Partial<ClinicOnboardingData>): Promise<ClinicProfile> {
    try {
      const clinic = await clinicApi.reviewSubmit(data);
      return clinic;
    } catch (error) {
      throw error;
    }
  }

  // Additional clinic management methods

  /**
   * Get clinic statistics
   */
  static async getStats(clinicId?: string): Promise<ClinicStats> {
    try {
      const stats = await clinicApi.getStats(clinicId);
      return stats;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get all doctors associated with clinic
   */
  static async getDoctors(params?: {
    status?: ClinicDoctorStatus;
    role?: ClinicDoctorRole;
    page?: number;
    limit?: number;
  }): Promise<{ doctors: ClinicDoctorAssociation[]; total: number }> {
    try {
      const response = await clinicApi.getDoctors(params);
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get single doctor details
   */
  static async getDoctor(doctorId: string): Promise<ClinicDoctorAssociation> {
    try {
      const doctor = await clinicApi.getDoctor(doctorId);
      return doctor;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Invite doctor to clinic
   */
  static async inviteDoctor(data: {
    email: string;
    role?: ClinicDoctorRole;
    message?: string;
  }): Promise<{ message: string; invitationId: string }> {
    try {
      const response = await clinicApi.inviteDoctor(data);
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Approve doctor request to join clinic
   */
  static async approveDoctor(doctorId: string, role?: ClinicDoctorRole): Promise<ClinicDoctorAssociation> {
    try {
      const doctor = await clinicApi.approveDoctor(doctorId, role);
      return doctor;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Reject doctor request to join clinic
   */
  static async rejectDoctor(doctorId: string, reason?: string): Promise<{ message: string }> {
    try {
      const response = await clinicApi.rejectDoctor(doctorId, reason);
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update doctor role or status
   */
  static async updateDoctor(
    doctorId: string,
    data: {
      role?: ClinicDoctorRole;
      status?: ClinicDoctorStatus;
    }
  ): Promise<ClinicDoctorAssociation> {
    try {
      const doctor = await clinicApi.updateDoctor(doctorId, data);
      return doctor;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Remove doctor from clinic
   */
  static async removeDoctor(doctorId: string, reason?: string): Promise<{ message: string }> {
    try {
      const response = await clinicApi.removeDoctor(doctorId, reason);
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get clinic activities/activity log
   */
  static async getActivities(params?: {
    type?: string;
    doctorId?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
  }): Promise<{ activities: ClinicActivity[]; total: number }> {
    try {
      const response = await clinicApi.getActivities(params);
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Request to join clinic (for doctors)
   */
  static async requestToJoin(clinicId: string, message?: string): Promise<{ message: string }> {
    try {
      const response = await clinicApi.requestToJoin(clinicId, message);
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get all staff members associated with clinic
   * GET /staff
   */
  static async getStaff(params?: {
    status?: 'ACTIVE' | 'INACTIVE';
    role?: 'DOCTOR' | 'NURSE' | 'RECEPTIONIST';
    page?: number;
    limit?: number;
  }): Promise<{
    staff: any[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> {
    try {
      const response = await clinicApi.getStaff(params);
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get single staff member details
   */
  static async getStaffMember(userId: string): Promise<ClinicStaffAssociation> {
    try {
      const staff = await clinicApi.getStaffMember(userId);
      return staff;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Invite staff member to clinic
   */
  static async inviteStaff(data: {
    email: string;
    role: 'doctor' | 'receptionist' | 'admin';
    message?: string;
  }): Promise<{ message: string; invitationId: string }> {
    try {
      const response = await clinicApi.inviteStaff(data);
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update staff member role or status
   */
  static async updateStaff(
    userId: string,
    data: {
      role?: 'doctor' | 'receptionist' | 'admin';
      status?: ClinicStaffStatus;
    }
  ): Promise<ClinicStaffAssociation> {
    try {
      const staff = await clinicApi.updateStaff(userId, data);
      return staff;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Remove staff member from clinic
   */
  static async removeStaff(userId: string, reason?: string): Promise<{ message: string }> {
    try {
      const response = await clinicApi.removeStaff(userId, reason);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Staff Management Methods

  /**
   * Create new staff member
   */
  static async createStaff(data: {
    email: string;
    role: 'doctor' | 'receptionist' | 'admin';
    firstName?: string;
    lastName?: string;
    phone?: string;
  }): Promise<ClinicStaffAssociation> {
    try {
      const staff = await clinicApi.createStaff(data);
      return staff;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Create new doctor (creates Staff + User) using Step 1 payload
   * Returns response with staff object containing _id
   */
  static async createDoctor(data: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    password: string;
    dob: string;
    gender: string;
    npiNumber?: string;
    basicInfo?: any;
    basicProfessionalInfo?: any;
    address?: any;
    profilePhoto?: any;
  }): Promise<{ staff: any }> {
    try {
      const response = await clinicApi.createDoctor(data);
      return response;
    } catch (error) {
      throw error;
    }
  }


  /**
   * Create new receptionist (creates Staff + User)
   * Returns response with staff object containing _id
   * Payload structure:
   * {
   *   firstName, lastName, email, phone, password, dob, gender (top level - required for validation)
   *   isPublishable, providerType, displayName (top level)
   *   basicInfo: {
   *     firstName, lastName, email, phone, dob, gender,
   *     profilePhoto: { cdnUrl, baseUrl, s3Url },
   *     address: { street, city, state, zip }
   *   }
   * }
   */
  static async createReceptionist(data: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    password: string;
    dob: string;
    gender: string;
    isPublishable?: boolean;
    providerType?: string;
    displayName?: string;
    basicInfo: {
      firstName: string;
      lastName: string;
      email: string;
      phone: string;
      dob: string;
      gender: string;
      profilePhoto?: {
        cdnUrl?: string;
        baseUrl?: string;
        s3Url?: string;
      };
      address?: {
        street?: string;
        city?: string;
        state?: string;
        zip?: string;
      };
    };
  }): Promise<{ staff: any }> {
    try {
      const response = await clinicApi.createReceptionist(data);
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get staff by ID
   */
  static async getStaffById(id: string): Promise<ClinicStaffAssociation> {
    try {
      const staff = await clinicApi.getStaffById(id);
      return staff;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update staff status
   */
  static async updateStaffStatus(id: string, status: ClinicStaffStatus): Promise<ClinicStaffAssociation> {
    try {
      const staff = await clinicApi.updateStaffStatus(id, status);
      return staff;
    } catch (error) {
      throw error;
    }
  }

  // Staff Onboarding - Doctor

  /**
   * Update doctor basic info (Step 1)
   * @param id - Clinic ID (from authenticated user)
   * @param data - Basic info data to update
   */
  static async updateDoctorBasicInfo(
    id: string, // Clinic ID
    data: Partial<ClinicStaffAssociation['profile']>
  ): Promise<ClinicStaffAssociation> {
    try {
      console.log(`[ClinicService.updateDoctorBasicInfo] Clinic ID: ${id}`);
      const staff = await clinicApi.updateDoctorBasicInfo(id, data);
      return staff;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update doctor credentials (Step 2)
   */
  static async updateDoctorCredentials(
    id: string,
    data: Partial<ClinicStaffAssociation['profile']>
  ): Promise<ClinicStaffAssociation> {
    try {
      const staff = await clinicApi.updateDoctorCredentials(id, data);
      return staff;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update doctor services (Step 3)
   */
  static async updateDoctorServices(
    id: string,
    data: Partial<ClinicStaffAssociation['profile']>
  ): Promise<ClinicStaffAssociation> {
    try {
      const staff = await clinicApi.updateDoctorServices(id, data);
      return staff;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update doctor pricing (Step 4)
   */
  static async updateDoctorPricing(
    id: string,
    data: Partial<ClinicStaffAssociation['profile']>
  ): Promise<ClinicStaffAssociation> {
    try {
      const staff = await clinicApi.updateDoctorPricing(id, data);
      return staff;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update doctor availability (Step 5)
   */
  static async updateDoctorAvailability(
    id: string,
    data: Partial<ClinicStaffAssociation['profile']>
  ): Promise<ClinicStaffAssociation> {
    try {
      const staff = await clinicApi.updateDoctorAvailability(id, data);
      return staff;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update doctor payment info (Step 6)
   */
  static async updateDoctorPayment(
    id: string,
    data: Partial<ClinicStaffAssociation['profile']>
  ): Promise<ClinicStaffAssociation> {
    try {
      const staff = await clinicApi.updateDoctorPayment(id, data);
      return staff;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Submit doctor profile (Step 7)
   */
  static async submitDoctorProfile(
    id: string,
    data?: Partial<ClinicStaffAssociation['profile']>
  ): Promise<ClinicStaffAssociation> {
    try {
      const staff = await clinicApi.submitDoctorProfile(id, data);
      return staff;
    } catch (error) {
      throw error;
    }
  }


  // Staff Onboarding - Receptionist

  /**
   * Update receptionist basic info (Step 1)
   */
  static async updateReceptionistBasicInfo(
    id: string,
    data: Partial<ClinicStaffAssociation['profile']>
  ): Promise<ClinicStaffAssociation> {
    try {
      const staff = await clinicApi.updateReceptionistBasicInfo(id, data);
      return staff;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update receptionist credentials (Step 2)
   */
  static async updateReceptionistCredentials(
    id: string,
    data: Partial<ClinicStaffAssociation['profile']>
  ): Promise<ClinicStaffAssociation> {
    try {
      const staff = await clinicApi.updateReceptionistCredentials(id, data);
      return staff;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update receptionist schedule (Step 3)
   */
  static async updateReceptionistSchedule(
    id: string,
    data: Partial<ClinicStaffAssociation['profile']>
  ): Promise<ClinicStaffAssociation> {
    try {
      const staff = await clinicApi.updateReceptionistSchedule(id, data);
      return staff;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update receptionist permissions (Step 4)
   */
  static async updateReceptionistPermissions(
    id: string,
    data: Partial<ClinicStaffAssociation['profile']>
  ): Promise<ClinicStaffAssociation> {
    try {
      const staff = await clinicApi.updateReceptionistPermissions(id, data);
      return staff;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Submit receptionist profile (Step 5)
   */
  static async submitReceptionistProfile(
    id: string,
    data?: Partial<ClinicStaffAssociation['profile']>
  ): Promise<ClinicStaffAssociation> {
    try {
      const staff = await clinicApi.submitReceptionistProfile(id, data);
      return staff;
    } catch (error) {
      throw error;
    }
  }
}

export default ClinicService;
